import type { FC } from 'react';

import type { AssetsShareElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_PLAYLISTS,
  OBJECT_TYPE_PORTALS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { Icon } from '@iconik-io/uad';

import { Link } from '../../components/link/link';
import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import { getCanAccessPortals } from '../../ducks/settings/select_features';
import { portalBasePath } from '../../pages/portals/paths';
import { useAppSelector } from '../../store/react_redux_typed';
import { selectUserRoles } from '../../utils/get_user_roles';

import styles from './shares.module.css';

type ShareObjectColumnProps = {
  item: AssetsShareElasticSchemaWithIdNullable;
};

const goToLabel = (entity: string, title: string | null | undefined) =>
  title ? `Go to ${entity}: ${title}` : `Go to ${entity}`;

export const ShareObjectColumn: FC<ShareObjectColumnProps> = ({ item }) => {
  const canAccessPortals = useAppSelector(getCanAccessPortals);
  const { canReadPortals } = useAppSelector(selectUserRoles);

  if (item.object_type === OBJECT_TYPE_PLAYLISTS) {
    return (
      <TooltipTrigger
        tooltipText="Playlist share"
        placement="left"
        componentProps={{ role: 'img', 'aria-label': 'Playlist share' }}
      >
        <Icon
          name="playlist"
          className={styles.share_type_icon}
          aria-hidden="true"
        />
      </TooltipTrigger>
    );
  }

  if (item.object_type === OBJECT_TYPE_COLLECTIONS) {
    const label = goToLabel('collection', item.object_title);

    return (
      <TooltipTrigger tooltipText={label} placement="left">
        <Link to={`/collection/${item.object_id}`} aria-label={label}>
          <Icon
            name="collection"
            className={styles.share_type_icon}
            aria-hidden="true"
          />
        </Link>
      </TooltipTrigger>
    );
  }

  if (item.object_type === OBJECT_TYPE_PORTALS) {
    const icon = (
      <Icon
        name="portal"
        className={styles.share_type_icon}
        aria-hidden="true"
      />
    );

    if (!item.object_id || !canAccessPortals || !canReadPortals) {
      return (
        <TooltipTrigger
          tooltipText="Portal share"
          placement="left"
          componentProps={{ role: 'img', 'aria-label': 'Portal share' }}
        >
          {icon}
        </TooltipTrigger>
      );
    }

    const label = goToLabel('portal', item.object_title);

    return (
      <TooltipTrigger tooltipText={label} placement="left">
        <Link to={portalBasePath(item.object_id)} aria-label={label}>
          {icon}
        </Link>
      </TooltipTrigger>
    );
  }

  return null;
};

export default ShareObjectColumn;
